import React, { FC, useMemo, useRef } from 'react';
import { useButton, useFocusRing, useHover } from 'react-aria';

import { cn } from '../../lib/css/className/className';
import { HStack, Stack } from '../Stack';
import { useHStack } from '../../hooks/useStack';
import { ClickableIcon } from '../ClickableIcon';
import { IconClose, IconCloseSkinny } from '../../icons/colorless';
import { Spinner } from '../Spinner';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { useTheme } from '../../lib/theme';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import './Tag.css';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { SkinColor, SkinRole } from '../../lib/skin/getColorSkin/generated/types';

import type { TagProps, TagType } from './types';

const skinMap: Record<TagType, SkinRole | SkinColor> = {
    regular: 'message-lc',
    error: 'error-lc',
    success: 'success-lc',
    warning: 'warning-lc',
};

const cls = cn('Tag');

export const Tag: FC<TagProps> = function Tag({
    type = 'regular',
    isDisabled,
    title,
    className,
    onClose,
    tag,
    caption,
    isCaptionTruncated = true,
    onClick,
    'data-testid': dataTestId,
    isLoading = false,
    addonLeft,
    size = 's',
    ...restProps
}) {
    const ref = useRef<HTMLDivElement>(null);
    const { buttonProps: commonButtonProps, isPressed } = useButton(
        { ...restProps, isDisabled: isDisabled || !onClick, elementType: 'div', onPress: onClick },
        ref,
    );
    const {
        onMouseDown: _onMouseDown,
        onPointerDown: _onPointerDown,
        onTouchStart: _onTouchStart,
        /**
         * по событию `onMouseDown`, `onPointerDown` и `onTouchStart`
         * вызывается функцию `disableTextSelection`, которая не позволяет выделить текст
         * docs: https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/interactions/src/usePress.ts#L338
         */
        ...buttonPropsWithTextSelection
    } = commonButtonProps;
    // Если Tag является некликабельным, то передаем пропсы, которые позволяют выделять текст
    const buttonProps = onClick ? commonButtonProps : buttonPropsWithTextSelection;
    const isSelectableText = !onClick && !isDisabled;
    const { hoverProps, isHovered } = useHover({ isDisabled: isDisabled || !onClick });
    const { focusProps, isFocusVisible } = useFocusRing();

    const hasIcon = Boolean(!isDisabled && onClose);
    const stackClasses = useHStack({ gap: 'current-geometry' });

    const colorSkinClass = getColorSkin(skinMap[type], { isDisabled, isHovered });
    const sizeSkinClass = getSizeSkin(size);

    const textSize = 'current-main';

    const theme = useTheme();

    const { iconSize } = theme.sizeTokens[size];

    const { addonClasses } = useInternalVerticalAlignWithSkin({
        textSize,
        addonType: 'icon',
    });

    const iconNode = useMemo(() => {
        if (!hasIcon) {
            return null;
        }

        let iconElement;

        if (isLoading) {
            iconElement = <Spinner size={iconSize} color="current" />;
        } else if (iconSize === '12') {
            iconElement = <IconClose size={iconSize} />;
        } else {
            iconElement = <IconCloseSkinny size={iconSize} />;
        }

        return (
            <>
                <ClickableIcon
                    data-testid={dataTestId ? `${dataTestId}.close` : 'TagGroup.close'}
                    onClick={isLoading ? undefined : onClose}
                    color="gray"
                    role="supplementary"
                    className={cls('icon', {}, [addonClasses])}
                >
                    {iconElement}
                </ClickableIcon>
            </>
        );
    }, [hasIcon, dataTestId, isLoading, onClose, addonClasses, iconSize]);

    const titleText = useMemo(() => {
        if (title) return title;

        const tagString = typeof tag === 'string' ? tag : '';
        const captionString = typeof caption === 'string' ? caption : '';
        return [tagString, captionString].join(' ').trimEnd();
    }, [title, caption, tag]);

    const tagTextComponent = useMemo(() => {
        if (!tag) {
            return null;
        }

        return (
            <TextGenericWithSkin
                isTruncated={Boolean(caption)}
                skinSize={size}
                textSize={textSize}
                color="current"
                title={titleText}
            >
                {tag}
            </TextGenericWithSkin>
        );
    }, [caption, size, tag, titleText]);

    const captionComponent = useMemo(() => {
        if (!caption) {
            return null;
        }

        return (
            <TextGenericWithSkin
                skinSize={size}
                textSize="current-body"
                color="current"
                role="supplementary"
                title={titleText}
                isTruncated={isCaptionTruncated}
            >
                {caption}
            </TextGenericWithSkin>
        );
    }, [caption, size, titleText, isCaptionTruncated]);

    const combinedText = useMemo(
        () =>
            captionComponent ? (
                <HStack gap={'current-geometry'} className={cls('text')}>
                    {tagTextComponent}
                    {captionComponent}
                </HStack>
            ) : (
                tagTextComponent
            ),
        [captionComponent, tagTextComponent],
    );

    const addonLeftComponent = useMemo(() => {
        return (
            addonLeft && (
                <Stack className={cls('addonLeft', { disabled: isDisabled }, [addonClasses, colorSkinClass])}>
                    {addonLeft}
                </Stack>
            )
        );
    }, [addonClasses, addonLeft, isDisabled, colorSkinClass]);

    return (
        <div
            {...buttonProps}
            {...hoverProps}
            {...focusProps}
            ref={ref}
            data-testid={dataTestId ? dataTestId : `TagGroup.tag.${titleText}`}
            className={cls(
                {
                    size,
                    hovered: isHovered,
                    pressed: isPressed,
                    disabled: isDisabled,
                    focused: isFocusVisible,
                    'selectable-text': isSelectableText,
                },
                [sizeSkinClass, stackClasses, className, colorSkinClass],
            )}
        >
            {addonLeftComponent}
            {combinedText}
            {iconNode}
        </div>
    );
};
